"use client";

import { lazy, Suspense, useState } from "react";
import type { ReportableError } from "src/lib/ErrorReporting";
import type { SupportTicketDraft } from "../NewSupportTicketDialog/NewSupportTicketDialog";

// Share the loader so hovering or focusing a trigger warms the dialog's lazy import.
const loadSupportDialog = () => import("../NewSupportTicketDialog/NewSupportTicketDialog");
const NewSupportTicketDialog = lazy(loadSupportDialog);
const preloadSupportDialog = () => {
  void loadSupportDialog().catch(() => undefined);
};

/**
 * Opens the support dialog with a screenshot of the underlying page, captured while the form is open.
 * Render `dialog` once and call `open` from a button.
 */
export const useSupportDialog = (error?: ReportableError, initialValues?: SupportTicketDraft) => {
  const [visible, setVisible] = useState(false);
  const [opened, setOpened] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [screenshot, setScreenshot] = useState<string>();
  const [screenshotSize, setScreenshotSize] = useState<{ width: number; height: number }>();

  const open = async () => {
    if (capturing) return;
    const size = { width: window.innerWidth, height: window.innerHeight };
    setScreenshotSize(size);
    setOpened(true);
    setVisible(true);
    setScreenshot(undefined);
    setCapturing(true);
    try {
      const { capturePageScreenshot } = await import("src/lib/Screenshot");
      setScreenshot(await capturePageScreenshot(size));
    } catch {
      // Screenshot capture is optional; the form remains usable if it fails to load.
    } finally {
      setCapturing(false);
    }
  };

  return {
    preload: preloadSupportDialog,
    open,
    capturing,
    dialog: opened ? (
      <Suspense fallback={null}>
        <NewSupportTicketDialog
          visible={visible}
          onVisibleChange={setVisible}
          error={error}
          initialValues={initialValues}
          screenshot={screenshot}
          screenshotLoading={capturing}
          screenshotSize={screenshotSize}
          onCreated={() => {}}
        />
      </Suspense>
    ) : null,
  };
};
